import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { BookOpen, FileText, Check, X, ArrowLeft, Zap, Clock, Award, BookMarked, Lightbulb, FileCheck, BarChart2, Target, FileCode, BookKey, Brain, Download, MessageCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DPPNotesInfoPage = () => {
  const features = [
    {
      icon: BookOpen,
      title: 'Comprehensive Coverage',
      description: 'Complete syllabus coverage for Class 11 & 12 Physics, aligned with JEE, NEET, and Board exams.'
    },
    {
      icon: FileText,
      title: 'Daily Practice Problems',
      description: 'Carefully curated DPPs with varying difficulty levels to strengthen problem-solving skills.'
    },
    {
      icon: BookMarked,
      title: 'Concise Notes',
      description: 'Well-structured, exam-focused notes that highlight key concepts, formulas, and important derivations.'
    },
    {
      icon: FileCheck,
      title: 'Solved Examples',
      description: 'Step-by-step solutions to help understand problem-solving approaches.'
    },
    {
      icon: BarChart2,
      title: 'Performance Tracking',
      description: 'Track your progress with topic-wise performance analysis.'
    },
    {
      icon: Zap,
      title: 'Quick Revision',
      description: 'Chapter-wise summary sheets for last-minute revision before exams.'
    }
  ];

  const chapters = [
    'Mechanics (Kinematics, Laws of Motion, Work Energy & Power)',
    'Rotational Motion & Gravitation',
    'Properties of Matter & Fluid Mechanics',
    'Thermodynamics & Kinetic Theory',
    'Oscillations & Waves',
    'Electrostatics & Current Electricity',
    'Magnetism & Electromagnetic Induction',
    'Optics (Ray & Wave)',
    'Modern Physics (Dual Nature, Atoms, Nuclei)',
    'Semiconductor Electronics',
    'Communication Systems'
  ];

  const pricingPlans = [
    {
      name: 'Per Module',
      price: '₹199',
      description: 'Ideal for focused topic preparation',
      features: [
        'Complete notes for one module',
        'DPPs with detailed solutions',
        'Important formulas & concepts',
        'Practice questions with answers',
        'Lifetime access to purchased module',
        'Perfect for targeted study'
      ],
      cta: 'Get Module',
      popular: false
    },
    {
      name: 'Full Access',
      price: '₹699',
      description: 'Complete access to all 5 modules',
      features: [
        'All 5 modules included',
        'Comprehensive study materials',
        'Complete DPPs with solutions',
        'Full formula sheets',
        'Practice tests & mock exams',
        'Best value - Save ₹300',
        'Lifetime access to all content'
      ],
      cta: 'Get Full Access',
      popular: true
    }
  ];

  const whatsIncluded = [
    'Chapter-wise PDF notes with detailed explanations',
    'Daily Practice Problems (DPPs) with step-by-step solutions',
    'Important formulas and derivations for quick revision',
    'Previous years\' board exam questions with solutions',
    'NCERT and NCERT exemplar problems with solutions',
    'Chapter-wise tests to track your progress',
    'Full-length mock tests for exam simulation',
    'Performance analytics and improvement areas',
    'Common mistakes and how to avoid them',
    'Exam strategies and time management tips'
  ];

  const notIncluded = [
    'Live doubt-solving sessions',
    'Personalized mentorship (available separately)',
    'Printed materials (digital access only)',
    'Video lectures (available in Premium)',
    'Test series (Board exams only, available separately)'
  ];

  const howItWorks = [
    {
      step: '1',
      title: 'Get Notified',
      description: 'Sign up to be notified when the DPP & Study Notes become available.',
      icon: Target
    },
    {
      step: '2',
      title: 'Instant Digital Access',
      description: 'Get immediate access to all materials in your chosen package after payment.',
      icon: FileCode
    },
    {
      step: '3',
      title: 'Download & Practice',
      description: 'Access and download chapter notes, DPPs, and practice tests to study at your own pace.',
      icon: Download
    },
    {
      step: '4',
      title: 'Ace Your Exams',
      description: 'Use our comprehensive materials and practice tests to boost your preparation and performance.',
      icon: Award
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>DPP & Study Notes - Physics Notebook</title>
        <meta name="description" content="Access comprehensive Daily Practice Problems (DPP) and study notes for Class 11 & 12 Physics, JEE, and NEET preparation." />
      </Helmet>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-4xl md:text-5xl font-semibold text-gray-900 mb-6"
          >
            DPP & Study Notes
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-2xl text-[#123B7A] font-medium mb-6"
          >
            Master Physics with Structured Practice
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-gray-600 max-w-4xl mx-auto"
          >
            Access our comprehensive collection of Daily Practice Problems (DPP) and concise study notes designed to help you master Physics concepts and excel in your exams.
          </motion.p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-semibold text-gray-900 mb-4">How It Works</h2>
            <div className="w-20 h-1 bg-[#123B7A] mx-auto mb-12"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {howItWorks.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-6 rounded-xl shadow-md text-center"
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <step.icon className="w-8 h-8 text-[#123B7A]" />
                  </div>
                  <div className="w-8 h-8 bg-[#123B7A] text-white rounded-full flex items-center justify-center text-sm font-bold mx-auto -mt-8 mb-2">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-semibold text-gray-900 mb-4">Why Choose Our DPP & Notes?</h2>
            <div className="w-20 h-1 bg-[#123B7A] mx-auto mb-12"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-100"
                >
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <feature.icon className="w-6 h-6 text-[#123B7A]" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">{feature.title}</h3>
                  <p className="text-gray-600 text-center">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Notification Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-semibold text-gray-900 mb-4">Coming Soon</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Be the first to know when our DPP & Study Notes are available for purchase.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              className="bg-[#123B7A] hover:bg-blue-700 text-white px-8 py-3 text-lg"
            >
              <a
                href="https://wa.me/919309242445?text=Hi%20there!%20I'd%20like%20to%20be%20notified%20when%20the%20DPP%20&%20Study%20Notes%20are%20available."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 no-underline"
              >
                <MessageCircle className="h-5 w-5" />
                Notify Me
              </a>
            </Button>
            
            <Button 
              asChild
              variant="outline" 
              className="px-8 py-3 text-lg border-gray-300 hover:border-gray-400"
            >
              <a
                href="mailto:aditya@physicsnotebook.in?subject=Notify%20me%20when%20DPP%20&%20Study%20Notes%20are%20available"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 no-underline text-gray-700 hover:text-gray-900"
              >
                <Mail className="h-5 w-5" />
                Email Notification
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-semibold text-gray-900 mb-4">What's in Each Module?</h2>
            <p className="text-xl text-gray-600">Comprehensive coverage of all key physics concepts</p>
            <div className="w-20 h-1 bg-[#123B7A] mx-auto mt-4"></div>
          </div>

          <div className="space-y-8">
            {/* Module 1 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-lg mr-4">
                  <Zap className="w-6 h-6 text-[#123B7A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Module 1: Mechanics</h3>
              </div>
              <p className="text-gray-700 mb-4">The backbone of Physics — motion, forces, energy, and rotation.</p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Includes:</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-700">
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Motion in a Straight Line</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Motion in a Plane</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Laws of Motion</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Work, Energy & Power</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>System of Particles and Rotational Motion</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Gravitation</span>
                  </li>
                </ul>
              </div>
              <p className="mt-3 text-sm text-gray-600 font-medium">
                💡 <span className="font-semibold">Core skills:</span> Newtonian thinking, problem solving, vector application, and visualization of motion.
              </p>
            </div>

            {/* Module 2 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-lg mr-4">
                  <svg className="w-6 h-6 text-[#123B7A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.486M7 17h.01"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Module 2: Oscillations & Waves</h3>
              </div>
              <p className="text-gray-700 mb-4">The bridge between mechanics and sound — repetitive motion and energy transfer.</p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Includes:</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-700">
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Simple Harmonic Motion (SHM)</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Damped & Forced Oscillations</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Resonance</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Wave Motion</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Sound Waves</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Beats & Doppler Effect</span>
                  </li>
                </ul>
              </div>
              <p className="mt-3 text-sm text-gray-600 font-medium">
                💡 <span className="font-semibold">Core skills:</span> Equation of motion in SHM, phase concept, wave speed, superposition, and energy in waves.
              </p>
            </div>

            {/* Module 3 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-lg mr-4">
                  <svg className="w-6 h-6 text-[#123B7A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Module 3: Properties of Matter & Thermodynamics</h3>
              </div>
              <p className="text-gray-700 mb-4">Exploring the physical behavior of matter and energy exchange.</p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Includes:</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-700">
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Mechanical Properties of Solids</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Mechanical Properties of Fluids</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Thermal Properties of Matter</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Thermodynamics</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Kinetic Theory of Gases</span>
                  </li>
                </ul>
              </div>
              <p className="mt-3 text-sm text-gray-600 font-medium">
                💡 <span className="font-semibold">Core skills:</span> Elasticity, pressure, flow, heat transfer, laws of thermodynamics, molecular interpretation.
              </p>
            </div>

            {/* Module 4 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-lg mr-4">
                  <svg className="w-6 h-6 text-[#123B7A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Module 4: Electrodynamics</h3>
              </div>
              <p className="text-gray-700 mb-4">Charges, currents, fields, and energy flow — the heart of modern physics applications.</p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Includes:</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-700">
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Electric Charges & Fields</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Electrostatic Potential & Capacitance</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Current Electricity</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Moving Charges & Magnetism</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Magnetism & Matter</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Electromagnetic Induction & Alternating Current</span>
                  </li>
                </ul>
              </div>
              <p className="mt-3 text-sm text-gray-600 font-medium">
                💡 <span className="font-semibold">Core skills:</span> Field mapping, potential-energy relationships, circuit laws, induction concepts.
              </p>
            </div>

            {/* Module 5 */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-lg mr-4">
                  <svg className="w-6 h-6 text-[#123B7A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Module 5: Optics & Modern Physics</h3>
              </div>
              <p className="text-gray-700 mb-4">Light, quantum ideas, and the atomic world — from lenses to electrons.</p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Includes:</h4>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-700">
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Ray Optics</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Wave Optics</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Dual Nature of Radiation and Matter</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Atoms & Nuclei</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Semiconductor Electronics</span>
                  </li>
                </ul>
              </div>
              <p className="mt-3 text-sm text-gray-600 font-medium">
                💡 <span className="font-semibold">Core skills:</span> Ray tracing, interference, diffraction, quantum effects, and electronic devices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-semibold text-gray-900 mb-4">What You'll Get</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our DPP & Notes package is designed to give you everything you need for exam success
            </p>
            <div className="w-20 h-1 bg-[#123B7A] mx-auto mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="bg-blue-100 p-2 rounded-lg mr-4">
                  <BookKey className="w-6 h-6 text-[#123B7A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Study Materials</h3>
              </div>
              <ul className="space-y-3">
                {whatsIncluded.slice(0, 5).map((item, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-md border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="bg-blue-100 p-2 rounded-lg mr-4">
                  <Brain className="w-6 h-6 text-[#123B7A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Practice & Assessment</h3>
              </div>
              <ul className="space-y-3">
                {whatsIncluded.slice(5).map((item, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Important Note</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>This is a self-study package. For personalized guidance, check out our <Link to="/mentorship-info" className="font-medium text-yellow-800 underline">Mentorship Program</Link> which includes these materials plus one-on-one sessions with expert mentors.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DPPNotesInfoPage;
